/**
 * Anlagen im Pruefschritt (Plan API 2026-11-01/01, E18) — zusaetzliche PDFs (Zeitnachweise, Lieferscheine …) als
 * BG-24 in `invoice.attachments`. Sie reisen in allen drei Ausgaben im Erzeugen-Aufruf mit; keine eigene Route,
 * keine Extra-Einheit.
 *
 * Grenzen = die Schwellen, ab denen die API `FIELD_TOO_LONG` warnt (`invoice-openapi.ts`, Attachment Schema BG-24,
 * `content`): 20 971 520 Base64-Zeichen je Anlage (15 MiB), 41 943 040 zusammen (30 MiB). Die Seite lehnt darueber
 * vorher ab — ein Dokument, das ein spaeteres Release mit 400 abweist, soll gar nicht erst entstehen.
 */

/** Hoechstens so viele Anlagen je Rechnung (E18). */
export const MAX_ATTACHMENTS = 10
/** Base64-Zeichen je Anlage, ab denen die API warnt (15 MiB dekodiert). */
export const ATTACHMENT_MAX_BASE64 = 20_971_520
/** Base64-Zeichen aller Anlagen zusammen, ab denen die API warnt (30 MiB dekodiert). */
export const ATTACHMENTS_TOTAL_MAX_BASE64 = 41_943_040
/**
 * BT-123 (Beschreibung): das BG-24-Schema der API fuehrt dafuer keine `.max()`; 200 wie dieselbe BT-123 im
 * Belegverweis-Schema (`supportingDocumentSchema.description`).
 */
export const ATTACHMENT_DESCRIPTION_MAX = 200

export interface ReviewAttachment {
  /** Nur fuer die Liste (React-Schluessel, Entfernen) — reist nicht. */
  id: string
  filename: string
  size: number
  /** Base64 ohne `data:`-Vorspann. */
  content: string
  description: string
}

export type AttachmentRejection = 'type' | 'size' | 'total' | 'count'

/** Laenge des Base64 einer Datei dieser Groesse — ohne sie zu lesen. */
export const base64Length = (bytes: number): number => 4 * Math.ceil(bytes / 3)

const isPdf = (file: { name: string; type: string }) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

/**
 * Warum eine Datei nicht angehaengt wird (`null` = sie passt). Geprueft vor dem Lesen, an der Groesse — eine
 * 40-MiB-Datei wird nie in Base64 gewandelt.
 */
export function rejectAttachment(
  file: { name: string; type: string; size: number },
  current: readonly ReviewAttachment[]
): AttachmentRejection | null {
  if (!isPdf(file)) return 'type'
  if (current.length >= MAX_ATTACHMENTS) return 'count'
  const length = base64Length(file.size)
  if (length > ATTACHMENT_MAX_BASE64) return 'size'
  const total = current.reduce((sum, a) => sum + a.content.length, 0)
  if (total + length > ATTACHMENTS_TOTAL_MAX_BASE64) return 'total'
  return null
}

/** BG-24 in der Form des API-Schemas: `filename`, `mimeType`, `content`, `description` (BT-123). */
export function toInvoiceAttachments(list: readonly ReviewAttachment[]) {
  return list.map((a) => ({
    filename: a.filename,
    mimeType: 'application/pdf',
    content: a.content,
    ...(a.description.trim() !== '' ? { description: a.description.trim() } : {}),
  }))
}
